import {Component, computed, signal, Signal} from '@angular/core';
import {Apollo} from 'apollo-angular';
import {DesktopHost, EditingWorkspace, Organization, Query, RepositoryProcessPayload} from '../../types-repository';
import {
  GET_HOSTS_AND_WORKSPACES,
  MOUNT_EDITING_WORKSPACE,
  SETUP_DESKTOP_HOST,
  START_DESKTOP_HOST,
  STOP_DESKTOP_HOST,
  UNMOUNT_EDITING_WORKSPACE
} from '../../queries-repository';
import {ContextService} from '../../services/context.service';
import {toObservable, toSignal} from '@angular/core/rxjs-interop';
import {of} from 'rxjs';
import {catchError, filter, map, switchMap, tap} from 'rxjs/operators';
import {BreadCrumb, LogoAreaComponent} from '../logo-area/logo-area.component';
import {HeadlineSectionComponent} from '../headline-section/headline-section.component';
import {CommonModule} from '@angular/common';
import {RouterLink, RouterOutlet} from '@angular/router';
import {CldatePipe} from '../../pipes/cldate.pipe';
import {IsAllowedAnywherePipe} from '../../pipes/has-action-pipe';
import {BannerService} from '../../banner/banner.service';
import {Dialog} from '@angular/cdk/dialog';
import {ConfirmDialogComponent} from '../../confirm-dialog/confirm-dialog.component';
import {MountDialogComponent} from './mount-dialog.component';
import {ProcessButtonService} from '../../services/process-button.service';
import {ProcessButtonDirective} from '../../process-button.directive';

interface HostView {
  host: DesktopHost;
  workspaces: EditingWorkspace[];
}

interface RegionView {
  region: string;
  city: string;
  unassigned: EditingWorkspace[];
  hosts: HostView[];
}

const ACTIVE_WORKSPACE_STATUSES = new Set([
  'RESOLVING', 'RESOLVED', 'STAGING', 'STAGED',
  'WAITING_FOR_EDITOR', 'MOUNTED', 'UNMOUNTED', 'COPYING_BACK', 'FAILED',
]);


@Component({
  selector: 'app-crd-management',
  templateUrl: './crd-management.component.html',
  styleUrls: ['./crd-management.component.scss'],
  imports: [
    HeadlineSectionComponent,
    LogoAreaComponent,
    CommonModule,
    CldatePipe,
    RouterLink,
    RouterOutlet,
    ProcessButtonDirective,
    IsAllowedAnywherePipe,
  ]
})
export class CrdManagementComponent {
  organization: Signal<Organization>;
  breadCrumbs: Signal<BreadCrumb[]>;

  loading$ = signal(false);
  combined$!: Signal<{ hosts: DesktopHost[]; workspaces: EditingWorkspace[] } | undefined>;
  regionView$!: Signal<RegionView[]>;


  constructor(
    private apollo: Apollo,
    private contextService: ContextService,
    private banner: BannerService,
    private dialog: Dialog,
    public pb: ProcessButtonService,
  ) {
    this.organization = this.contextService.organization;

    this.breadCrumbs = computed(() => [{
      display: 'Content Platform',
      commands: ['/o', this.organization()?.id],
    }]);

    this.combined$ = toSignal(
      toObservable(this.contextService.organization).pipe(
        switchMap((org) => {
          if (!org) return of(undefined);
          const hostsPool = this.extractPool('contentplatform:repository:Query/DesktopHosts');
          const workspacesPool = this.extractPool('contentplatform:repository:Query/EditingWorkspaces');
          return this.apollo.watchQuery<Query>({
            query: GET_HOSTS_AND_WORKSPACES,
            variables: { hostsInput: { pool: hostsPool }, workspacesInput: { pool: workspacesPool } },
            pollInterval: 15000,
            fetchPolicy: 'network-only',
            notifyOnNetworkStatusChange: true,
          }).valueChanges.pipe(
            tap(result => this.loading$.set(result.loading)),
            filter(result => !!result.data),
            map(result => {
              const hosts = (result.data?.desktopHosts || []) as DesktopHost[];
              // Clear stop spinners when instance reaches stopped/stopping state
              for (const h of hosts) {
                if ((h.state === 'stopped' || h.state === 'stopping') && this.pb.isActive('stop-' + h.instanceId)) {
                  this.pb.clear('stop-' + h.instanceId);
                }
              }
              return {
                hosts,
                workspaces: (result.data?.editingWorkspaces || []) as EditingWorkspace[],
              };
            }),
            catchError((error) => {
              this.loading$.set(false);
              this.banner.open(`Error loading desktop hosts/workspaces: ${error?.message || 'Network error'}`, ['Close']);
              return of(undefined);
            })
          );
        })
      ),
    );

    this.regionView$ = computed(() => {
      const hosts = this.combined$()?.hosts || [];
      const workspaces = (this.combined$()?.workspaces ?? []) as EditingWorkspace[];

      const regionSet = new Set<string>();
      hosts.forEach(h => regionSet.add(h.awsRegion));
      workspaces.filter(w => ACTIVE_WORKSPACE_STATUSES.has(w.status)).forEach(w => { if (w.awsRegion) regionSet.add(w.awsRegion); });

      return [...regionSet].sort().map(region => {
        const regionHosts = hosts
          .filter(h => h.awsRegion === region)
          .sort((a, b) => (a.pool || '').localeCompare(b.pool || '') || (a.name || a.instanceId).localeCompare(b.name || b.instanceId));
        const regionWorkspaces = workspaces.filter(w => w.awsRegion === region && ACTIVE_WORKSPACE_STATUSES.has(w.status));
        return {
          region,
          city: this.regionCity(region),
          hosts: regionHosts.map(host => ({ host, workspaces: regionWorkspaces.filter(w => w.mountedOn === host.instanceId) })),
          unassigned: regionWorkspaces.filter(w => !w.mountedOn),
        };
      });
    });
  }

  formatTimestamp(timestamp: number): Date {
    return new Date(timestamp * 1000);
  }

  startSession(instance: DesktopHost) {
    const buttonId = 'start-' + instance.instanceId;
    this.pb.setPending(buttonId);
    this.apollo.mutate({
      mutation: START_DESKTOP_HOST,
      variables: {
        input: {
          pool: instance.pool,
          hostInstance: instance.instanceId,
          region: instance.awsRegion,
        }
      }
    }).subscribe({
      next: (result) => {
        const process = (result.data as any)?.startDesktopHost as RepositoryProcessPayload;
        if (process?.id) {
          const name = instance.name || instance.instanceId;
          this.pb.bind(buttonId, process, (state, p) => {
            if (state === 'FAILED') this.banner.open(`Setup of ${name} failed: ${p.message || 'Unknown error'}`, ['Close']);
          });
        } else {
          this.pb.clear(buttonId);
        }
      },
      error: (error) => {
        this.pb.clear(buttonId);
        const name = instance.name || instance.instanceId;
        this.banner.open(`Failed to start ${name}: ${error?.message || 'Unknown error'}`, ['Close']);
      }
    });
  }


  setupSession(instance: DesktopHost) {
    const buttonId = 'setup-' + instance.instanceId;
    this.pb.setPending(buttonId);
    this.apollo.mutate({
      mutation: SETUP_DESKTOP_HOST,
      variables: {
        input: {
          pool: instance.pool,
          hostInstance: instance.instanceId,
          region: instance.awsRegion,
        }
      }
    }).subscribe({
      next: (result) => {
        const process = (result.data as any)?.setupDesktopHost as RepositoryProcessPayload;
        if (process?.id) {
          const name = instance.name || instance.instanceId;
          this.pb.bind(buttonId, process, (state, p) => {
            if (state === 'FAILED') this.banner.open(`Setup of ${name} failed: ${p.message || 'Unknown error'}`, ['Close']);
          });
        } else {
          this.pb.clear(buttonId);
        }
      },
      error: (error) => {
        this.pb.clear(buttonId);
        const name = instance.name || instance.instanceId;
        this.banner.open(`Failed to set up ${name}: ${error?.message || 'Unknown error'}`, ['Close']);
      }
    });
  }

  stopSession(instance: DesktopHost) {
    const name = instance.name || instance.instanceId;
    this.dialog.open(ConfirmDialogComponent, {
      data: {
        headline: 'Stop Desktop Host',
        message: `Do you really want to stop <strong>${name}</strong>?`,
        actionName: 'Stop',
        action: () => {
          this.pb.setPending('stop-' + instance.instanceId);
          this.apollo.mutate({
            mutation: STOP_DESKTOP_HOST,
            variables: {
              input: {
                pool: instance.pool,
                hostInstance: instance.instanceId,
                region: instance.awsRegion,
              }
            }
          }).subscribe({
            next: () => {},
            error: (error) => {
              this.pb.clear('stop-' + instance.instanceId);
              this.banner.open(`Failed to stop ${name}: ${error?.message || 'Unknown error'}`, ['Close']);
            },
          });
        }
      }
    });
  }

  /** Extract pool value from policy resources for a given action.
   *  e.g. resource "contentplatform:pool:input.pool/*" → "*",
   *       resource "contentplatform:pool:input.pool/above_above" → "above_above" */
  private extractPool(action: string): string {
    const policies = this.contextService.policies();
    const prefix = 'contentplatform:pool:input.pool/';
    for (const policy of policies) {
      for (const statement of policy.statements) {
        if (statement.actions.includes(action)) {
          for (const resource of statement.resources) {
            if (resource.startsWith(prefix)) {
              return resource.substring(prefix.length);
            }
          }
        }
      }
    }
    return null;
  }



  getEditStatusClass(status: string): string {
    switch (status) {
      case 'STAGED': case 'WAITING_FOR_EDITOR': case 'MOUNTED': return 'cl-color-leaf';
      case 'RESOLVING': case 'RESOLVED': case 'STAGING': case 'COPYING_BACK': return 'cl-color-honey';
      case 'FAILED': return 'cl-color-brick';
      default: return 'cl-color-ash';
    }
  }

  getEditStatusLabel(status: string): string {
    switch (status) {
      case 'RESOLVING': case 'RESOLVED': return 'Provisioning';
      case 'STAGING': return 'Copying content';
      case 'STAGED': case 'WAITING_FOR_EDITOR': return 'Ready';
      case 'MOUNTED': return 'Mounted';
      case 'UNMOUNTED': return 'Detached';
      case 'COPYING_BACK': return 'Saving changes';
      case 'FAILED': return 'Failed';
      default: return status;
    }
  }

  isMounting(ws: EditingWorkspace): boolean { return this.pb.isActive('mount-' + ws.mountPoint); }

  volumeId(ws: EditingWorkspace): string {
    return ws.id.split('/').pop() || ws.id;
  }

  regionCity(region: string): string {
    const cities: Record<string, string> = {
      'eu-west-1': 'Dublin', 'eu-central-1': 'Frankfurt', 'eu-south-2': 'Madrid',
      'us-east-1': 'Virginia', 'us-west-2': 'Oregon', 'af-south-1': 'Cape Town',
    };
    return cities[region] || region;
  }

  isMountable(ws: EditingWorkspace): boolean {
    return ['STAGED', 'WAITING_FOR_EDITOR', 'UNMOUNTED'].includes(ws.status);
  }

  hostsInSameAz(ws: EditingWorkspace): DesktopHost[] {
    return (this.combined$()?.hosts || []).filter(
      h => (h.state === 'registered' || h.state === 'in_use') && h.availabilityZone === ws.availabilityZone
    );
  }

  showMountDialog(ws: EditingWorkspace) {
    const hosts = this.hostsInSameAz(ws);
    this.dialog.open(MountDialogComponent, {
      data: {
        workspace: ws,
        hosts,
        onMount: (host: DesktopHost, done: (success: boolean) => void) => {
          this.mountWorkspaceWithCallback(ws, host, done);
        },
      },
    });
  }

  private mountWorkspaceWithCallback(ws: EditingWorkspace, host: DesktopHost, done: (success: boolean) => void) {
    const buttonId = 'mount-' + ws.mountPoint;
    this.pb.setPending(buttonId);
    this.apollo.mutate({
      mutation: MOUNT_EDITING_WORKSPACE,
      variables: {
        input: {
          pool: ws.pool,
          id: ws.id,
          hostInstance: host.instanceId,
        }
      }
    }).subscribe({
      next: (result) => {
        const process = (result.data as any)?.mountEditingWorkspace as RepositoryProcessPayload;
        if (process?.id) {
          this.pb.bind(buttonId, process, (state, p) => {
            if (state === 'FAILED') this.banner.open(`Failed to mount ${ws.mountPoint}: ${p.message || 'Unknown error'}`, ['Close']);
            done(state === 'SUCCESS');
          });
        } else {
          this.pb.clear(buttonId);
          done(false);
        }
      },
      error: (error) => {
        this.pb.clear(buttonId);
        this.banner.open(`Failed to mount ${ws.mountPoint}: ${error?.message || 'Unknown error'}`, ['Close']);
        done(false);
      },
    });
  }

  unmountWorkspace(ws: EditingWorkspace, action: 'UNMOUNT' | 'COPY_BACK' | 'DELETE') {
    const name = ws.mountPoint || ws.id;
    const mountInfo = ws.mountedOn ? ` It is currently mounted as <strong>${ws.driveLetter}:</strong> on <strong>${ws.mountedOn}</strong>.` : '';
    const messages: Record<string, string> = {
      'UNMOUNT': `Unmount <strong>${name}</strong> and keep the volume. The content remains on disk and can be remounted later.${mountInfo}`,
      'COPY_BACK': `Copy the content of <strong>${name}</strong> back to S3, then delete the volume.${mountInfo}`,
      'DELETE': `Permanently delete the volume <strong>${name}</strong> and all its content. This cannot be undone.${mountInfo}`,
    };
    const actionNames: Record<string, string> = {
      'UNMOUNT': 'Unmount',
      'COPY_BACK': 'Copy back and delete',
      'DELETE': 'Delete',
    };
    const headlines: Record<string, string> = {
      'UNMOUNT': `Unmount ${name}`,
      'COPY_BACK': `Copy back and delete ${name}`,
      'DELETE': `Delete ${name}`,
    };
    this.dialog.open(ConfirmDialogComponent, {
      data: {
        headline: headlines[action],
        message: messages[action],
        actionName: actionNames[action],
        danger: action === 'DELETE' || action === 'COPY_BACK',
        action: () => {
          const buttonId = 'unmount-' + ws.id + '-' + action;
          this.pb.setPending(buttonId);
          this.apollo.mutate({
            mutation: UNMOUNT_EDITING_WORKSPACE,
            variables: {
              input: {
                pool: ws.pool,
                id: ws.id,
                action,
              }
            }
          }).subscribe({
            next: (result) => {
              const process = (result.data as any)?.unmountEditingWorkspace as RepositoryProcessPayload;
              if (process?.id) {
                this.pb.bind(buttonId, process, (state, p) => {
                  if (state === 'FAILED') this.banner.open(`Failed to unmount ${ws.mountPoint}: ${p.message || 'Unknown error'}`, ['Close']);
                });
              } else {
                this.pb.clear(buttonId);
              }
            },
            error: (error) => {
              this.pb.clear(buttonId);
              this.banner.open(`Failed to unmount ${ws.mountPoint}: ${error?.message || 'Unknown error'}`, ['Close']);
            },
          });
        }
      }
    });
  }
}
